import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function ControlAnafAutoturismPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/control-anaf-cheltuieli-autoturism")} />
      <ArticleLayout
        eyebrow="Pregătire control"
        title="Control ANAF pentru cheltuielile cu autoturismele: ce documente trebuie să ai pregătite"
        lead="La un control pe cheltuieli auto, inspectorii nu se uită doar la foaia de parcurs. Verifică dacă dosarul e coerent: facturi, bonuri, asigurări, ITP, contracte și dovezi că mașina a fost folosită în scop economic — nu personal."
      >
        <p>
          Pe scurt: pentru deductibilitatea cheltuielilor cu autoturismul de firmă, pregătește un
          dosar complet — foaie de parcurs (dacă pretinzi 100%), bonuri și facturi de combustibil,
          documente ale vehiculului (ITP, asigurări), contracte (leasing, comodat, regulament
          intern) și o poveste clară despre utilizarea economică versus personală.
        </p>

        <h2>Ce documente verifică ANAF la cheltuielile cu autoturismul?</h2>
        <ol>
          <li>Foi de parcurs (când deduci 100%)</li>
          <li>Bonuri / facturi de combustibil și alte cheltuieli auto</li>
          <li>ITP și asigurări (RCA, CASCO dacă există)</li>
          <li>Contracte: leasing, comodat, folosință, regulament intern</li>
          <li>Corelația între km, consum, rezervor și documentele de plată</li>
        </ol>
        <p>
          Detaliile despre cele 4 elemente minime ale foii și diferența 50% / 100% le găsești în{" "}
          <Link to="/ghid-foaie-parcurs-anaf">ghidul despre cerințele ANAF</Link> și în{" "}
          <Link to="/deducere-100-combustibil">articolul despre deducerea 100%</Link>. Mai jos
          ne concentrăm pe <strong>dosarul de control</strong>, nu pe teoria fiscală.
        </p>

        <h2>Ce verifică, de fapt, inspectorii</h2>
        <p>
          Controlul pe cheltuieli auto e o verificare de <strong>coerență</strong>. Nu e suficient
          să ai o foaie frumos formatată. Inspectorul compară surse diferite și caută
          contradicții.
        </p>

        <h3>Plăți vs. kilometri</h3>
        <p>
          Alimentările din bonuri se potrivesc cu kilometrii și capacitatea rezervorului?
          Formula simplă: litri alimentați ≈ km × norma de consum.
        </p>

        <h3>Scopul deplasărilor</h3>
        <p>
          Traseele din foaie au legătură cu clienți, furnizori, sedii, instituții? Sau par
          inventate? Scopul trebuie să fie concret și verificabil.
        </p>

        <h3>Dreptul de folosință</h3>
        <p>
          Mașina e în patrimoniu, leasing sau pe numele administratorului? Există contract
          care justifică cheltuielile pe CUI?
        </p>

        <h3>Utilizare exclusiv economică</h3>
        <p>
          Există un{" "}
          <Link to="/regulament-intern-autoturism">regulament intern</Link> sau alte dovezi
          că nu e folosită zilnic pentru trasee personale fără evidență?
        </p>

        <h2>Rolul foii de parcurs în dosar</h2>
        <p>
          Foaia de parcurs este documentul care leagă <strong>deplasările</strong> de{" "}
          <strong>deducerea integrală</strong>. Fără ea (sau cu o foaie incoerentă), regimul de
          100% e greu de susținut — chiar dacă ai toate bonurile de combustibil.
        </p>
        <p>
          La control, foaia nu stă singură: trebuie să „bată" cu facturile, cu odometrul și cu
          activitatea firmei. O foaie fără documente de plată sau cu scopuri vagi e un semnal de
          alarmă, nu o protecție.
        </p>

        <h2>Checklist: documentele justificative pe care să le ai pregătite</h2>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Document</th>
                <th>De ce se cere</th>
                <th>Ce verifică inspectorul</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Foaie de parcurs</td>
                <td>Justifică utilizarea exclusiv economică (deducere 100%)</td>
                <td>Scop, loc, km, consum; coerență pe lună / perioadă</td>
              </tr>
              <tr>
                <td>Bonuri / facturi combustibil</td>
                <td>Dovedesc cheltuiala reală</td>
                <td>Dată, litri, valoare, legătura cu vehiculul și cu foaia</td>
              </tr>
              <tr>
                <td>Facturi service, piese, rovinietă, parcare</td>
                <td>Susțin celelalte cheltuieli auto deduse</td>
                <td>Dacă apar pe firmă și dacă se leagă de același vehicul</td>
              </tr>
              <tr>
                <td>ITP</td>
                <td>Confirmă că vehiculul e în circulație legală</td>
                <td>Valabilitate pe perioada cheltuielilor</td>
              </tr>
              <tr>
                <td>Asigurări (RCA, CASCO)</td>
                <td>Cheltuieli auto tipice; identifică vehiculul</td>
                <td>Polița pe firmă / pe utilizatorul declarat</td>
              </tr>
              <tr>
                <td>Contract leasing / comodat / folosință</td>
                <td>Arată dreptul de a folosi mașina în activitate</td>
                <td>Cine plătește, cine folosește, pe ce perioadă</td>
              </tr>
              <tr>
                <td>Regulament intern / politică auto</td>
                <td>Susține interzicerea sau limitarea folosirii personale</td>
                <td>Dacă există și dacă e aplicat în practică</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Bonuri de combustibil și facturi — ce contează la control</h2>
        <p>
          Bonurile trebuie să poată fi asociate vehiculului și perioadei din foaie. Probleme
          tipice: bonuri pe alt CUI, card personal fără decont clar, alimentări care depășesc
          rezervorul sau care nu lasă loc pentru km declarați.
        </p>
        <p>
          Facturile (service, anvelope, asigurări) întăresc povestea că mașina e un activ al
          activității. Dacă deduci totul 100%, dar nu ai nici foaie coerentă, nici regulament,
          inspectorul poate trece ușor la recalculare.
        </p>

        <h2>ITP, asigurări și contracte</h2>
        <p>
          ITP-ul și RCA-ul nu „înlocuiesc" foaia de parcurs. Ele arată că vehiculul există, e
          legal în trafic și e legat de firmă. Lipsa lor pe perioada controlată ridică semne de
          întrebare (cheltuieli pe o mașină care nu putea circula legal).
        </p>
        <p>
          La leasing sau comodat, contractul clarifică cine are dreptul de folosință. Fără el,
          e greu să explici de ce cheltuielile apar pe firmă, iar mașina e pe alt nume.
        </p>

        <h2>Utilizare în scop economic vs. personal — cum se vede la control</h2>
        <p>
          Diferența nu e doar teoretică. Inspectorul caută indicii practice:
        </p>
        <ul>
          <li>
            trasee către clienți, depozite, instituții, șantiere — versus trasee tipice
            personale fără legătură cu activitatea;
          </li>
          <li>
            weekend-uri și vacanțe acoperite doar cu „deplasare serviciu" vagă;
          </li>
          <li>
            un singur utilizator care locuiește departe de sediu, fără evidență a navetei
            personale.
          </li>
        </ul>
        <p>
          Dacă folosești mașina mixt, regimul de 50% e adesea calea mai sigură. Dacă pretinzi
          100%, dosarul trebuie să susțină exclusivitatea — foaie + documente + reguli interne.
        </p>

        <h2>Greșeli frecvente</h2>

        <h3>Foaie perfectă, dosar gol</h3>
        <p>
          Ai km și scopuri pe foaie, dar lipsesc bonurile sau contractul de folosință.
          Foaia singură nu dovedește nimic fără documentele suport.
        </p>

        <h3>Bonuri fără legătură cu foaia</h3>
        <p>
          Date și litri care nu se potrivesc cu traseele sau cu rezervorul. Inspectorul
          compară alimentările cu kilometrii declarați.
        </p>

        <h3>ITP sau asigurare expirate</h3>
        <p>
          Documente expirate pe lunile în care deduci combustibil și deplasări ridică
          întrebări despre legalitatea circulației.
        </p>

        <h3>Mașină pe persoană fizică, cheltuieli pe firmă</h3>
        <p>
          Fără comodat sau document clar de folosință, cheltuielile pe CUI nu au
          justificare legală.
        </p>

        <h3>Completare în grabă înainte de control</h3>
        <p>
          Același tipar pe luni întregi, fără variație realistă. Inspectorii observă
          foile făcute la grămadă, cu aceeași cerneală și scris.
        </p>

        <h3>Confuzie între 50% și 100%</h3>
        <p>
          Pretinzi deducere integrală, dar recunoști (sau reiese) folosirea personală.
          Regimul trebuie să fie consistent cu realitatea.
        </p>

        <div className="article-doc__callout article-doc__callout--warning">
          <strong>Atenție</strong>
          <p>
            Recalcularea deducerii pe mai mulți ani, plus dobânzi și penalități, costă de obicei
            mai mult decât timpul investit într-un dosar ținut la zi.
          </p>
        </div>

        <h2>Sfaturi practice</h2>

        <h3>Dosar pe vehicul</h3>
        <p>
          Ține la un loc: contract, polițe, ITP, foi de parcurs, bonuri și extrase card
          combustibil. Un dosar organizat scurtează controlul.
        </p>

        <h3>Actualizare lunară</h3>
        <p>
          Completează foile lunar, nu „când vine controlul". Coerența cu bonurile
          contemporane e mult mai ușor de demonstrat.
        </p>

        <h3>Verificare periodică</h3>
        <p>
          O dată pe lună: total km foaie ≈ diferență odometru; litri alimentați ≈ consum
          estimat. 5 minute care previn surprize.
        </p>

        <h3>Scanări organizate</h3>
        <p>
          Păstrează PDF-uri pe an și lună. Ușurează predarea către contabil și răspunsul
          rapid la solicitări.
        </p>

        <h3>Discută cu contabilul</h3>
        <p>
          Dacă situația e mixtă, vorbește cu contabilul înainte să forțezi 100% fără
          dovezi solide.
        </p>

        <h2>Concluzie</h2>
        <p>
          Un control ANAF pe cheltuielile cu autoturismele se câștigă cu un dosar coerent, nu cu
          un singur document. Foaia de parcurs e esențială pentru deductibilitatea integrală, dar
          bonurile, facturile, ITP-ul, asigurările și contractele completează povestea.
        </p>
        <p>
          Dacă vrei ca foile să rămână la zi și coerente cu alimentările — fără ore pierdute în
          Excel — poți folosi o aplicație dedicată. AIFoiParcurs te ajută să completezi perioadele
          lipsă și să exporti rezultatul pentru contabil.
        </p>
        <RelatedGuides currentPath="/control-anaf-cheltuieli-autoturism" />
      </ArticleLayout>
    </>
  );
}
